import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ClipboardIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  EyeIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  LightBulbIcon,
  LinkIcon,
  CameraIcon,
  RocketLaunchIcon,
  XMarkIcon,
  HandRaisedIcon,
  ArrowRightIcon,
  VideoCameraIcon,
  MegaphoneIcon,
} from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';
import {
  fetchChecklistProgress,
  markChecklistItem,
  fetchCreatorStats,
  fetchAdminSuggestions,
  fetchBioProofCount,
  uploadBioProof,
  hasCustomizedLink,
  hasSponsorEnabled,
  hasSentChatMessage,
  fetchRecentViewCount,
  CreatorStats,
  AdminSuggestion,
} from '../services/creatorDashboard';
import { supabase } from '../services/supabase';
import toast from 'react-hot-toast';
import ContentCategoryModal from './ContentCategoryModal';

interface CreatorHubDashboardProps {
  talentProfile: { id: string; username?: string };
  onNavigate: (view: string) => void;
}

const CHECKLIST_ITEMS = [
  {
    key: 'customize_link',
    label: 'Finish customizing your ShoutOut Fans link',
    description: 'Add links, themes, and content to your bio page.',
    actionLabel: 'Customize',
    actionType: 'navigate' as const,
    actionTarget: 'customize',
  },
  {
    key: 'earn_50_bio',
    label: 'Earn $30 by using your link exclusively in your bio on 2 platforms',
    description: 'Upload 2 screenshots of your profile showing the link.',
    actionLabel: 'Done!',
    actionType: 'done' as const,
    actionTarget: '',
  },
  {
    key: 'enable_sponsors',
    label: 'Turn on Pre-Paid Sponsors to earn $600-$1,200 per 1000 clicks',
    description: 'Enable sponsor ads on your bio page.',
    actionLabel: 'Enable',
    actionType: 'navigate' as const,
    actionTarget: 'customize',
  },
  {
    key: 'complete_course',
    label: 'Earn $20 by answering a few questions',
    description: 'Quick survey that helps us improve ShoutOut for you.',
    actionLabel: "Let's do it!",
    actionType: 'navigate' as const,
    actionTarget: 'course',
  },
];

const SOCIAL_NETWORKS = [
  { id: 'youtube', label: 'YouTube' },
  { id: 'rumble', label: 'Rumble' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'twitter', label: 'Twitter / X' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'truth_social', label: 'Truth Social' },
  { id: 'telegram', label: 'Telegram' },
  { id: 'gettr', label: 'GETTR' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'podcast', label: 'Podcast' },
  { id: 'snapchat', label: 'Snapchat' },
  { id: 'other', label: 'Other' },
];

const ESTABLISHED_CREATOR_HIDDEN_ITEMS = [
  'earn_50_bio',
  'complete_course',
];

// The two cash steps are paused while the team decides whether the bonuses
// stay. Creators who already earned one still see it; for everyone else the
// row is hidden, which is also what closes the earning path — the proof and
// course flows are only reachable from these rows.
const PAUSED_BONUS_ITEMS = ['earn_50_bio', 'complete_course'];

/**
 * The rows this creator actually sees. Both the render and the `allDone`
 * calculation must agree on this — if `allDone` counts a row the creator
 * cannot see, a finished checklist reads as unfinished forever.
 */
function visibleChecklistFor(
  completed: Record<string, boolean>,
  isEstablished: boolean
) {
  return CHECKLIST_ITEMS.filter(i => {
    if (isEstablished && ESTABLISHED_CREATOR_HIDDEN_ITEMS.includes(i.key)) return false;
    if (PAUSED_BONUS_ITEMS.includes(i.key) && !completed[i.key]) return false;
    return true;
  });
}

// Summary line to show next to each completed checklist item after the whole
// checklist is done. Helps creators see exactly which steps earned them money
// so the total on the Earnings card isn't a mystery.
const CHECKLIST_REWARDS: Record<string, { amount: number | null; label: string }> = {
  customize_link: { amount: null, label: 'Bio customized' },
  earn_50_bio: { amount: 30, label: '$30 · Bio proof (pending approval)' },
  enable_sponsors: { amount: null, label: 'Pre-paid sponsors enabled' },
  complete_course: { amount: 20, label: '$20 · Onboarding course' },
  story_promo: { amount: null, label: 'Promo requested' },};

// Text shown in the announcement banner at the top of the hub. Kept in code
// (rather than sourced from the #announcements chat) so it can be updated
// without editing chat history. Supports **bold** and blank-line paragraphs.
const ANNOUNCEMENT_BANNER_TEXT =
  '**Notice: your personal data is safe.** We do not touch or host bank account or personal address/information which is all done through 3rd party applications like Moov.io and Plaid.com and is completely safe.\n\n' +
  'We will be working with a 3rd party Cyber Security company to help creators scan the dark web and remove their data if found, due to the uptick in attacks on conservative companies and creators that also got hacked recently like Daily Wire, RNC, Rumble, Tucker Carlson, Steven Crowder, and Alex Jones.\n\n' +
  'For more information on the recent incident please see the announcement channel.';

// Creators (by lowercased username) who should see the fixed custom
// data-security notice (ANNOUNCEMENT_BANNER_TEXT) instead of the latest
// #announcements chat message. Sourced from the incident recipient list;
// comparison is case-insensitive. Everyone NOT in this set sees the most
// recent #announcements channel message. Move a username out of this set to
// switch that creator over to the live announcement-channel notice.
const ANNOUNCEMENT_ALLOWED_USERNAMES = new Set<string>([
  'pearldavis', 'shawnfarash', 'meghanelinor', 'kristinsokoloff', 'kaitlinbennett',
  'notmelissavitelli', '50696950500506i5', 'chrissalcedo', 'basrutten', 'rachel_wilson_bhm',
  'd', 'thepolitidoxy', 'lifelibertylacey', 'ariellescarcella', 'rissalesi',
  'onlypatriotjake', 'ggg', 'sassyliberty', 'magameg4547', 'shessoright',
  'theauthentichousewife', 'kanoadaddy', 'projectlibertyus', 'whitesquirrelmagic', 'tommynutz',
  'thepoliticalmixster', 'realtaylorsharp', 'livingright26', 'calimagabarbie', '1234567',
  'samueljacobcomedy', 'mercerdylanusa', 'yankeenews1776', 'abraydon', 'patrioticpeach',
  'gunnarthorderson', 'dylanjamesofficial', 'countrylivingconservative', 'realnicolagan', 'kym',
  'pragmaticplantlady', 'conservativechocolatevet', 'brittanybee', 'basedinmalibu', 'kayoffscript',
  'hazelstruth', 'ishofarabia', 'therepublicanbarbie', 'nickyrudd', 'kaitlincolegate',
  'evanslearingcenter', 'waltonwisdom', 'mcgrawshauna', 'truth_seeker_babe', 'kaiklips',
  'justjenrx', 'thatpatriotteacher', 'probablyreganlong', 'thebarbieporter', 'koltonsmitty',
  'gentrygevers', 'adamfrancisco', 'audreyku', 'conjayreacts', 'chattyannieofficial',
  'jerrygolddd',
]);

const CreatorHubDashboard: React.FC<CreatorHubDashboardProps> = ({ talentProfile, onNavigate }) => {
  const [stats, setStats] = useState<CreatorStats>({ totalEarnings: 0, pendingApprovalEarnings: 0, totalViews: 0, avgCtr: 0 });
  const [suggestions, setSuggestions] = useState<AdminSuggestion[]>([]);
  const [checklistCompleted, setChecklistCompleted] = useState<Record<string, boolean>>({});
  const [animatingItem, setAnimatingItem] = useState<string | null>(null);
  const [showProofUpload, setShowProofUpload] = useState(false);
  const [proofCount, setProofCount] = useState(0);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [followerInputs, setFollowerInputs] = useState<Record<string, number>>({});
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [chatBannerDismissed, setChatBannerDismissed] = useState(() => localStorage.getItem('chat_banner_dismissed') === 'true');
  const [allDone, setAllDone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [isEstablishedCreator, setIsEstablishedCreator] = useState(false);
  const [isPartner, setIsPartner] = useState<boolean | null>(null);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [agreedToPartnerTerms, setAgreedToPartnerTerms] = useState(false);
  const [enrollingPartner, setEnrollingPartner] = useState(false);
  const [showContentCategoryModal, setShowContentCategoryModal] = useState(false);
  const [announcement, setAnnouncement] = useState<{ id: string; content: string; created_at: string } | null>(null);
  const [announcementDismissed, setAnnouncementDismissed] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const bioUrl = `shoutout.fans/${talentProfile.username || talentProfile.id}`;

  const loadDashboardData = useCallback(async () => {
    try {
      const [progressMap, creatorStats, adminSuggestions, bioProofCt, linkCustomized, sponsorsOn, alreadyChatted, recentViews, profileFlagsRes] = await Promise.all([
        fetchChecklistProgress(talentProfile.id),
        fetchCreatorStats(talentProfile.id),
        fetchAdminSuggestions(talentProfile.id),
        fetchBioProofCount(talentProfile.id),
        hasCustomizedLink(talentProfile.id),
        hasSponsorEnabled(talentProfile.id),
        hasSentChatMessage(talentProfile.id),
        fetchRecentViewCount(talentProfile.id, 30),
        supabase
          .from('talent_profiles')
          .select('auto_apply_sponsors, targeting_category')
          .eq('id', talentProfile.id)
          .single(),
      ]);

      const established = recentViews > 300;
      setIsEstablishedCreator(established);

      // Force a content-category pick for pre-paid-sponsor creators who've
      // shown engagement (posted in #creators chat OR 10+ bio views) but
      // haven't told us what they post — we need it to target sponsors.
      const flags = profileFlagsRes?.data;
      const sponsorOptIn = !!flags?.auto_apply_sponsors || sponsorsOn;
      const needsTargetingCategory =
        sponsorOptIn &&
        !flags?.targeting_category &&
        (alreadyChatted || (creatorStats.totalViews ?? 0) >= 10);
      setShowContentCategoryModal(needsTargetingCategory);

      const completed: Record<string, boolean> = {};
      Object.keys(progressMap).forEach(key => { completed[key] = true; });
      if (linkCustomized) completed['customize_link'] = true;
      if (sponsorsOn) completed['enable_sponsors'] = true;
      // Mark earn_50_bio complete as soon as 2 screenshots are uploaded.
      // Actual $50 payout still gates on admin approval, but the checklist
      // shouldn't feel stuck after the creator has uploaded both proofs.
      if (bioProofCt >= 2) completed['earn_50_bio'] = true;

      setChecklistCompleted(completed);
      setStats(creatorStats);
      setSuggestions(adminSuggestions);
      setProofCount(bioProofCt);

      const visibleItems = visibleChecklistFor(completed, established);
      setAllDone(visibleItems.every(item => completed[item.key]));
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [talentProfile.id]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Load the most recent admin announcement to surface at the top of the hub.
  // Mirrors CreatorChat's "last announcement" lookup (default announcements
  // channel) so the card always reflects the newest post in chat.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: ch } = await supabase
          .from('chat_channels')
          .select('id')
          .eq('channel_type', 'announcements')
          .eq('is_default', true)
          .single();
        if (!ch || cancelled) return;
        const { data: msgs } = await supabase
          .from('chat_messages')
          .select('id, content, created_at')
          .eq('channel_id', ch.id)
          .eq('is_deleted', false)
          .is('parent_message_id', null)
          .eq('message_type', 'text')
          .order('created_at', { ascending: false })
          .limit(1);
        const latest = msgs?.[0];
        if (!latest || cancelled) return;
        setAnnouncement({ id: latest.id, content: latest.content, created_at: latest.created_at });
        if (localStorage.getItem('hub_announcement_dismissed') === latest.id) {
          setAnnouncementDismissed(true);
        }
      } catch {
        // non-critical; silently ignore
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Real-time subscriptions: re-fetch earnings when payouts or sponsor clicks change
  useEffect(() => {
    const channel = supabase
      .channel(`creator-earnings-${talentProfile.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'payouts', filter: `talent_id=eq.${talentProfile.id}` },
        () => loadDashboardData()
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'sponsor_clicks', filter: `talent_id=eq.${talentProfile.id}` },
        () => loadDashboardData()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [talentProfile.id, loadDashboardData]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.rpc('get_partner_for_talent', { p_talent_id: talentProfile.id });
        if (cancelled) return;
        if (error) { setIsPartner(false); return; }
        setIsPartner(!!(data && (data as any).is_active));
      } catch {
        if (!cancelled) setIsPartner(false);
      }
    })();
    return () => { cancelled = true; };
  }, [talentProfile.id]);

  const handleEnrollAsPartner = useCallback(async () => {
    if (!agreedToPartnerTerms || enrollingPartner) return;
    if (!talentProfile?.id) {
      toast.error('Missing talent profile');
      return;
    }
    setEnrollingPartner(true);
    try {
      const { error } = await supabase.rpc('self_enroll_as_partner', {
        p_talent_id: talentProfile.id,
        p_agreement_version: 'v1',
      });
      if (error) throw error;
      setIsPartner(true);
      setShowPartnerModal(false);
      setAgreedToPartnerTerms(false);
      window.dispatchEvent(new CustomEvent('partner-enrolled'));
      toast.success('You are now a ShoutOut partner! Check the Partner tab.');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to activate partner account';
      toast.error(msg);
    } finally {
      setEnrollingPartner(false);
    }
  }, [agreedToPartnerTerms, enrollingPartner, talentProfile?.id]);


  const handleChecklistAction = async (item: typeof CHECKLIST_ITEMS[0]) => {
    if (checklistCompleted[item.key]) return;

    if (item.actionType === 'navigate') {
      onNavigate(item.actionTarget);
      return;
    }

    if (item.key === 'earn_50_bio') {
      setShowProofUpload(true);
      return;
    }

    try {
      await markChecklistItem(talentProfile.id, item.key);
      setAnimatingItem(item.key);
      setTimeout(() => {
        setChecklistCompleted(prev => ({ ...prev, [item.key]: true }));
        setAnimatingItem(null);
        const newCompleted = { ...checklistCompleted, [item.key]: true };
        if (visibleChecklistFor(newCompleted, isEstablishedCreator).every(i => newCompleted[i.key])) {
          setAllDone(true);
        }
      }, 800);
      // Re-fetch stats so newly-awarded checklist earnings show up immediately
      // (DB triggers create the payout row after markChecklistItem; give them a beat).
      setTimeout(() => { loadDashboardData(); }, 1500);
      toast.success('Marked as done!');
    } catch (error) {
      console.error('Error marking checklist item:', error);
      toast.error('Failed to update');
    }
  };

  const handleProofUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingProof(true);
    try {
      await uploadBioProof(talentProfile.id, file, `platform_${proofCount + 1}`);
      const newCount = proofCount + 1;
      setProofCount(newCount);
      toast.success(`Screenshot ${newCount}/2 uploaded!`);

      if (newCount >= 2) {
        setShowProofUpload(false);
        toast.success('Both screenshots uploaded! Awaiting admin approval for your $30 payout.');
        // Persist checklist completion so the step doesn't re-appear on reload.
        try {
          await markChecklistItem(talentProfile.id, 'earn_50_bio', { proof_count: newCount });
          setChecklistCompleted(prev => ({ ...prev, earn_50_bio: true }));
          // Save follower counts if the creator entered any
          const cleanedFollowers: Record<string, number> = {};
          Object.entries(followerInputs).forEach(([k, v]) => { if (v > 0) cleanedFollowers[k] = v; });
          if (Object.keys(cleanedFollowers).length > 0) {
            await supabase
              .from('talent_profiles')
              .update({ follower_counts: cleanedFollowers })
              .eq('id', talentProfile.id);
          }
          setTimeout(() => { loadDashboardData(); }, 1500);
        } catch (e) {
          console.error('Error marking earn_50_bio complete:', e);
        }
      }
    } catch (error) {
      console.error('Error uploading proof:', error);
      toast.error('Failed to upload screenshot');
    } finally {
      setUploadingProof(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`https://${bioUrl}`);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const dismissAnnouncement = () => {
    if (announcement) localStorage.setItem('hub_announcement_dismissed', announcement.id);
    setAnnouncementDismissed(true);
  };

  const formatAnnouncementTime = (ts: string) => {
    const d = new Date(ts);
    const diff = Date.now() - d.getTime();
    if (diff < 3600000) return `${Math.max(1, Math.floor(diff / 60000))}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 7 * 86400000) return `${Math.floor(diff / 86400000)}d ago`;
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Render the announcement body with **bold** markdown and line breaks.
  const renderAnnouncement = (text: string) =>
    text.split('\n').filter(line => line.trim() !== '').map((line, i) => (
      <p key={i}>
        {line.split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
          part.startsWith('**') && part.endsWith('**')
            ? <strong key={j} className="font-semibold text-white">{part.slice(2, -2)}</strong>
            : <React.Fragment key={j}>{part}</React.Fragment>
        )}
      </p>
    ));

  const visibleChecklistItems = visibleChecklistFor(checklistCompleted, isEstablishedCreator);
  const completedCount = visibleChecklistItems.filter(i => checklistCompleted[i.key]).length;

  // The ~66 incident-recipient creators keep the fixed custom data-security
  // notice; everyone else sees the latest #announcements channel message.
  const showCustomAnnouncement = ANNOUNCEMENT_ALLOWED_USERNAMES.has((talentProfile.username || '').toLowerCase());

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Latest admin announcement — surfaced from the #announcements chat */}
      {announcement && !announcementDismissed && (
        <div
          role="button"
          tabIndex={0}
          onClick={() => onNavigate('chat:announcements')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onNavigate('chat:announcements'); } }}
          className="relative w-full text-left cursor-pointer rounded-2xl overflow-hidden border border-amber-400/30 bg-gradient-to-br from-amber-500/10 via-fuchsia-500/[0.06] to-purple-600/10 hover:from-amber-500/[0.16] hover:to-purple-600/[0.16] transition-colors p-5"
        >
          <button
            onClick={(e) => { e.stopPropagation(); dismissAnnouncement(); }}
            className="absolute top-3 right-3 p-1 text-gray-500 hover:text-white transition-colors"
            aria-label="Dismiss announcement"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-400/15 flex items-center justify-center">
              <MegaphoneIcon className="h-5 w-5 text-amber-300" />
            </div>
            <div className="flex-1 min-w-0 pr-5">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-[11px] font-bold uppercase tracking-wider text-amber-300">Announcement</span>
                <span className="text-[11px] text-gray-500">{formatAnnouncementTime(announcement.created_at)}</span>
              </div>
              <div className="text-sm text-gray-200 space-y-1 line-clamp-none sm:line-clamp-6 whitespace-pre-line">
                {renderAnnouncement(showCustomAnnouncement ? ANNOUNCEMENT_BANNER_TEXT : announcement.content)}
              </div>
              <span className="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-amber-300">
                Read in chat <ArrowRightIcon className="h-3.5 w-3.5" />
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Header row */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-white">Creator Hub</h2>
        <button
          onClick={copyLink}
          className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/15 border border-white/20 rounded-xl text-sm font-semibold text-white transition-colors"
        >
          {copied ? (
            <CheckCircleIcon className="h-4 w-4 text-green-400" />
          ) : (
            <LinkIcon className="h-4 w-4 text-blue-400" />
          )}
          {copied ? 'Copied!' : 'Copy My Link'}
        </button>
      </div>

      {/* Checklist Section — always visible so completed creators can see the
          items (and $ amounts) they earned; when everything's done we flip
          the header to a celebratory "You earned $X" summary. */}
      {(() => {
        const checklistEarnings = visibleChecklistItems.reduce((sum, item) => {
          if (!checklistCompleted[item.key]) return sum;
          return sum + (CHECKLIST_REWARDS[item.key]?.amount || 0);
        }, 0);
        return (
        <div className="border border-white/20 rounded-2xl overflow-hidden bg-white/5 backdrop-blur-sm">
          <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <RocketLaunchIcon className={`h-6 w-6 ${allDone ? 'text-green-400' : 'text-amber-400'}`} />
              <h2 className="text-lg font-bold text-white">
                {allDone
                  ? (checklistEarnings > 0
                    ? `Setup complete — you earned $${checklistEarnings.toFixed(2)}`
                    : 'Setup complete')
                  : 'Get setup and start monetizing'}
              </h2>
            </div>
            <span className="text-sm text-gray-400">{completedCount}/{visibleChecklistItems.length}</span>
          </div>

          <div className="max-h-[400px] overflow-y-auto overscroll-contain divide-y divide-white/10" style={{ WebkitOverflowScrolling: 'touch' }}>
            {visibleChecklistItems.map((item) => {
              const isDone = checklistCompleted[item.key];
              const isAnimating = animatingItem === item.key;

              return (
                <div
                  key={item.key}
                  className={`px-5 py-4 flex items-start gap-4 transition-all duration-500 ${
                    isAnimating ? 'bg-green-500/20 scale-[0.98]' : isDone ? 'opacity-60' : ''
                  }`}
                >
                  <div className={`flex-shrink-0 mt-0.5 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-500 ${
                    isDone || isAnimating
                      ? 'border-green-500 bg-green-500'
                      : 'border-gray-500'
                  }`}>
                    {(isDone || isAnimating) && (
                      <CheckIcon className="h-4 w-4 text-white animate-bounce" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className={`font-medium ${isDone ? 'text-gray-500 line-through' : 'text-white'}`}>
                      {item.label}
                    </p>
                    {item.description && !isDone && (
                      <p className="text-sm text-gray-400 mt-0.5">{item.description}</p>
                    )}
                    {isDone && CHECKLIST_REWARDS[item.key] && (
                      <p className="text-sm text-green-400 mt-0.5">{CHECKLIST_REWARDS[item.key].label}</p>
                    )}
                  </div>

                  {!isDone && (
                    <button
                      onClick={() => handleChecklistAction(item)}
                      className="flex-shrink-0 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors"
                    >
                      {item.actionLabel}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        );
      })()}


      {/* Stats Row */}
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 sm:col-span-1 border border-white/20 rounded-2xl bg-white/5 px-5 py-4">
          <div className="flex items-center gap-2 mb-2">
            <CurrencyDollarIcon className="h-5 w-5 text-green-400" />
            <span className="text-sm text-gray-400">Total Earnings</span>
          </div>
          <p className="text-3xl font-bold text-white">${stats.totalEarnings.toFixed(2)}</p>
          {stats.pendingApprovalEarnings > 0 && (
            <p className="text-xs text-amber-300 mt-1">
              Includes ${stats.pendingApprovalEarnings.toFixed(2)} pending admin approval
            </p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3 col-span-2 sm:col-span-1">
          <div className="border border-white/20 rounded-2xl bg-white/5 px-4 py-4">
            <div className="flex items-center gap-1.5 mb-2">
              <EyeIcon className="h-4 w-4 text-blue-400" />
              <span className="text-xs text-gray-400">Views</span>
            </div>
            <p className="text-xl font-bold text-white">{stats.totalViews.toLocaleString()}</p>
          </div>
          <div className="border border-white/20 rounded-2xl bg-white/5 px-4 py-4">
            <div className="flex items-center gap-1.5 mb-2">
              <ChartBarIcon className="h-4 w-4 text-purple-400" />
              <span className="text-xs text-gray-400">Avg CTR</span>
            </div>
            <p className="text-xl font-bold text-white">{stats.avgCtr.toFixed(1)}%</p>
          </div>
        </div>
      </div>

      {/* Referral network card */}
      {isPartner === false && (
        <div className="border border-indigo-400/30 rounded-2xl bg-gradient-to-br from-indigo-500/10 via-purple-500/10 to-pink-500/10 p-5">
          <div className="flex items-center gap-2 mb-2">
            <HandRaisedIcon className="h-5 w-5 text-indigo-300" />
            <h3 className="font-semibold text-white">Want to join our referral network?</h3>
          </div>
          <p className="text-sm text-indigo-100/90 mb-2">
            Make $50 &amp; give $50 when the referred creator uses ShoutOut Fans.
          </p>
          <p className="text-sm text-indigo-100/70 mb-4">
            We're building the largest free-speech creator community on the planet. With an
            insatiable drive to help creators in the space monetize better without fear of
            cancel culture.
          </p>
          <button
            onClick={() => setShowPartnerModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-gray-900 text-sm font-semibold hover:bg-gray-100 transition-colors"
          >
            Become a partner
            <ArrowRightIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Partner disclosure modal */}
      {showPartnerModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-white/20 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">ShoutOut Partner Agreement</h3>
              <button
                onClick={() => { setShowPartnerModal(false); setAgreedToPartnerTerms(false); }}
                className="text-gray-400 hover:text-white"
                aria-label="Close"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-3 text-sm text-gray-300 mb-4">
              <p>
                By joining the ShoutOut Partner Program you agree to the following terms:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  You earn <span className="text-white font-medium">$50</span> when a creator you
                  refer adds their ShoutOut Fans link to their primary platform(s) exclusively
                  and it replaces their existing link-in-bio.
                </li>
                <li>
                  The referred creator also receives <span className="text-white font-medium">$50</span>,
                  paid out per ShoutOut's standard payout schedule once their account is set up.
                </li>
                <li>
                  Referrals must be attributed through your unique referral link. Self-referrals,
                  fraudulent signups, or incentivized signups that violate ShoutOut's terms are
                  not eligible for payout.
                </li>
                <li>
                  Payouts are made to a connected payout account (e.g. Moov) and may require
                  identity verification before funds can be released.
                </li>
                <li>
                  ShoutOut may modify, pause, or end the Partner Program or individual
                  partnerships at any time, and reserves the right to withhold payouts for
                  activity that violates these terms or our general Terms of Service.
                </li>
                <li>
                  You agree to represent ShoutOut honestly and in line with our brand &mdash; no
                  spam, no misleading claims about earnings, and no targeting of minors.
                </li>
                <li>
                  You are responsible for any taxes owed on payouts you receive as a partner.
                </li>
              </ul>
              <p className="text-xs text-gray-500">
                Agreement version v1. By clicking "Agree &amp; activate" you acknowledge you have
                read and accepted these terms and ShoutOut's general Terms of Service.
              </p>
            </div>

            <label className="flex items-start gap-2 mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={agreedToPartnerTerms}
                onChange={(e) => setAgreedToPartnerTerms(e.target.checked)}
                className="mt-1"
              />
              <span className="text-sm text-gray-200">
                I have read and agree to the ShoutOut Partner Program terms.
              </span>
            </label>

            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => { setShowPartnerModal(false); setAgreedToPartnerTerms(false); }}
                className="px-4 py-2 rounded-xl bg-white/10 text-white text-sm hover:bg-white/15 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEnrollAsPartner}
                disabled={!agreedToPartnerTerms || enrollingPartner}
                className="px-4 py-2 rounded-xl bg-indigo-500 text-white text-sm font-semibold hover:bg-indigo-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {enrollingPartner ? 'Activating…' : 'Agree & activate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Proof Upload Modal */}
      {showProofUpload && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-white/20 rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Upload Bio Screenshots</h3>
              <button onClick={() => setShowProofUpload(false)} className="text-gray-400 hover:text-white">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <p className="text-gray-400 text-sm mb-4">
              Upload screenshots of your profile on 2 platforms showing your ShoutOut Fans link in your bio.
              After admin approval, you'll receive a <span className="text-white font-semibold">$30 payout</span>.
            </p>

            <div className="flex items-center gap-2 mb-4">
              {[1, 2].map(n => (
                <div
                  key={n}
                  className={`flex-1 h-2 rounded-full ${
                    proofCount >= n ? 'bg-green-500' : 'bg-white/10'
                  }`}
                />
              ))}
            </div>
            <p className="text-sm text-gray-400 mb-4">{proofCount}/2 screenshots uploaded</p>

            {/* Follower count section */}
            <div className="mb-5">
              <p className="text-white text-sm font-semibold mb-1">Your follower count <span className="text-gray-500 font-normal">(optional)</span></p>
              <p className="text-gray-500 text-xs mb-3">Tell us how many followers you have on each platform where your link is live.</p>

              {/* Platform selector */}
              <div className="flex flex-wrap gap-2 mb-3">
                {SOCIAL_NETWORKS.map(n => {
                  const selected = selectedPlatforms.includes(n.id);
                  return (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => {
                        setSelectedPlatforms(prev =>
                          selected ? prev.filter(p => p !== n.id) : [...prev, n.id]
                        );
                        if (selected) {
                          setFollowerInputs(prev => { const u = { ...prev }; delete u[n.id]; return u; });
                        }
                      }}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                        selected
                          ? 'bg-blue-600 border-blue-500 text-white'
                          : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/30'
                      }`}
                    >
                      {n.label}
                    </button>
                  );
                })}
              </div>

              {selectedPlatforms.length > 0 && (
                <div className="space-y-2">
                  {selectedPlatforms.map(platformId => {
                    const network = SOCIAL_NETWORKS.find(n => n.id === platformId);
                    return (
                      <div key={platformId} className="flex items-center gap-2">
                        <span className="text-gray-400 text-xs w-24 flex-shrink-0">{network?.label}</span>
                        <input
                          type="number"
                          min={0}
                          placeholder="e.g. 12500"
                          value={followerInputs[platformId] || ''}
                          onChange={e => setFollowerInputs(prev => ({ ...prev, [platformId]: parseInt(e.target.value) || 0 }))}
                          className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-blue-500"
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {proofCount < 2 && (
              <label className="block">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProofUpload}
                  className="hidden"
                  disabled={uploadingProof}
                />
                <div className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-white/20 rounded-xl text-white cursor-pointer hover:border-blue-500 hover:bg-blue-500/10 transition-colors">
                  <CameraIcon className="h-5 w-5" />
                  {uploadingProof ? 'Uploading...' : `Upload Screenshot ${proofCount + 1}`}
                </div>
              </label>
            )}
          </div>
        </div>
      )}

      {/* Forced content-category pick for sponsor-enabled, engaged creators */}
      <ContentCategoryModal
        isOpen={showContentCategoryModal}
        talentId={talentProfile.id}
        onSaved={() => setShowContentCategoryModal(false)}
      />
    </div>
  );
};

export default CreatorHubDashboard;
